import { unit } from 'mathjs';

import { SPORT_TYPE, A_MAP_MAX_ZOOM_LEVEL, GOOGLE_MAP_MAX_ZOOM_LEVEL } from './constant';

import { convertToAMapCoordinate } from '../../utils/get-godv';
import {
  NoMarker,
  CalcSkiBoardAndSnowBoardMarkers,
  CalcIntervalTrainingMarkers,
  CalcNormalTrainingMarkers,
  CalcClimbMarkers,
  CalcMountainSkiingMarkers,
  CalcCombinationTrainingMarkers
} from './CalcMarkers';
import { binarySearch, checkIfIsSkiing, getScalePerPixel, getTargetLapType, normalizeGps } from './utils/marker';
import { isProgram, isInterval } from '../../../../hooks/useActivityDetail';
import { preProcessActivityIndex } from '../../../../main/table/utils/getIndexNew/getIndexNew';

export class MarkLap {
  constructor(isGoogle, map, unit) {
    this.map = map;
    this.isGoogleMap = isGoogle;
    this.laps = [];
    this.allMarkers = [];
    this.maximum4markers = 0;
    this.summary = {};
    this.currentUnit = unit;
    this.waitMapLoad();
  }

  async waitMapLoad() {
    this.basisMarkerConfig = {
      anchor: 'center'
    };
  }

  /**
   * 获取可渲染的圈标记
   * @returns {Array}
   */
  async getRenderableMarkers() {
    const calculations = [
      NoMarker,
      CalcClimbMarkers,
      CalcMountainSkiingMarkers,
      CalcSkiBoardAndSnowBoardMarkers,
      CalcCombinationTrainingMarkers,
      CalcIntervalTrainingMarkers,
      CalcNormalTrainingMarkers
    ].map((constructor) => new constructor(this.basisMarkerConfig, this.currentUnit));

    calculations.reduce((prev, curr) => {
      prev.setNext(curr);

      return curr;
    });

    const calculator = calculations[0];

    /**
     * 根据最大可显示圈标记数量，对所有圈标记分组取值
     * @param {Array} allMarkers 所有圈标记
     * @returns {Array}
     */
    const filterRenderableMarkers = (allMarkers) => {
      this.allMarkers = allMarkers;
      if (allMarkers.length <= this.maximum4markers) {
        return allMarkers;
      }

      const renderableMarkers = [allMarkers[allMarkers.length - 1]];
      const amount4group = Math.ceil(allMarkers.length / this.maximum4markers);
      let counter = 0;
      for (let index = allMarkers.length - 1; index > 0; index--) {
        if (counter >= amount4group) {
          counter = 0;
          renderableMarkers.push(allMarkers[index]);
        } else {
          counter++;
        }
      }
      return renderableMarkers;
    };

    if (!this.isGoogleMap) {
      await convertToAMapCoordinate(this.laps.map((lap) => lap._markerGps));
    }

    return calculator.exec(this.laps, this.summary, this.isGoogleMap).then(filterRenderableMarkers);
  }

  setSummary(summary) {
    this.summary = summary;
    return this;
  }

  /**
   * 查找给定时间戳两端最近的并且是有效的运动点
   * @param {Array} points 原始运动点
   * @param {Number} target 目标时间戳
   * @returns Object
   */
  findNearestPoint(points, target) {
    const index = binarySearch(points, target);

    const startIndex4greaterPoint = points[index]?.timestamp === target ? index + 1 : index;
    const greaterPoint = this.getMeaningfulPoint(points, startIndex4greaterPoint, 1);
    const startIndex4lowerPoint = index - 1;
    const lowerPoint = this.getMeaningfulPoint(points, startIndex4lowerPoint, -1);

    if (!lowerPoint || !greaterPoint) return lowerPoint || greaterPoint;

    if (Math.abs(greaterPoint.timestamp - target) < Math.abs(lowerPoint.timestamp - target)) {
      return greaterPoint;
    } else {
      return lowerPoint;
    }
  }

  /**
   * 获取有意义的点（经纬度不为0）
   * @param {Array} points 活动点集合
   * @param {Number} start 遍历起点
   * @param {Number} step 步进大小
   * @returns {Object} 运动点数据
   */
  getMeaningfulPoint(points, start, step) {
    for (let index = start; points[index] !== undefined; index = index + step) {
      const point = points[index];
      if (point.gpsLat !== 0 && point.gpsLon !== 0) return point;
    }
  }

  /**
   * 计算最大可展示的圈标记数：距离(米) / 当前地图像素比例尺（米/像素） / 标记占位大小(像素)
   * 滑雪运动和运动场跑步特殊处理：使用平均每趟/圈的距离作为分子
   * 地图当前缩放比例达到最大时，显示所有圈标记
   * @param {Array} allActivties 所有活动点
   * @returns {MarkLap}
   */
  calcMaximum4markers() {
    const zoomLevel = this.map.getZoom();
    if (
      (this.isGoogleMap && zoomLevel === GOOGLE_MAP_MAX_ZOOM_LEVEL) ||
      (this.isGoogleMap === false && zoomLevel === A_MAP_MAX_ZOOM_LEVEL)
    ) {
      this.maximum4markers = Number.POSITIVE_INFINITY;
      return this;
    }
    const zoomPercent = zoomLevel / (this.isGoogleMap ? GOOGLE_MAP_MAX_ZOOM_LEVEL : A_MAP_MAX_ZOOM_LEVEL);
    // 单位：米/像素
    const scalePerPixel = getScalePerPixel(zoomLevel);
    // 标记大小为16px，给一定的余量作为标记之间的间隙，余量为余缩放百分比相反，缩放比例为0.5时，余量为1个标记大小，缩放比例为1是，余量为0.5个标记大小
    const pixel4marker = 16 * (2.5 - zoomPercent);
    const meters4totalDistance = unit(this.summary.distance, 'cm').toNumber('m');
    // console.log(`🔔 meters4totalDistance`, meters4totalDistance);
    // console.log(`🔔 scalePerPixel`, scalePerPixel);
    // console.log(`🔔 pixel4marker`, pixel4marker);

    // Todo: 策略模式优化
    // 跑圈和滑雪的轨迹近似为一圈，所以特殊处理：使用平均每趟的距离作为计算分母
    if (checkIfIsSkiing(this.summary.sportType) || this.summary.sportType === SPORT_TYPE.SPORT_FIELD_RUNNING) {
      const averageMeters4lap = meters4totalDistance / this.laps.length;
      this.maximum4markers = Math.floor(averageMeters4lap / scalePerPixel / pixel4marker);
    } else {
      this.maximum4markers = Math.floor(meters4totalDistance / scalePerPixel / pixel4marker);
      if (this.maximum4markers <= 0) this.maximum4markers = 1;
    }
    // console.log(`🔔 set this.maximum4markers`, this.maximum4markers);

    return this;
  }

  /**
   * 计算可用于标记的圈数据：滑雪运动取起点坐标有效的，其他取终点坐标有效的
   * @param {Array} data 圈数据
   * @returns {MarkLap} markLap 实例
   */
  calcLaps2mark(data, allActivties) {
    const isSkiing = checkIfIsSkiing(this.summary.sportType);
    const coordinatePrefix = isSkiing ? 'start' : 'end';
    const lapType2mark = getTargetLapType(this.summary.sportType);
    // 圈标记点序号，与圈速表 useLapData.ts dealLapItemList 处理一致
    // 因为无法直接共享过来，这里单独一份
    let x_activityIndex = 0;
    const processLapData = (lap) => {
      const flag = preProcessActivityIndex({
        uniqueId: null,
        data: lap,
        isInterval: isInterval.value,
        isProgram: isProgram.value,
        isLap: true,
        isRunTest: this.summary.isRunTest === 1
      });
      if (flag) {
        x_activityIndex++;
        lap.x_activityIndex = x_activityIndex;
      }
      let lon;
      let lat;
      if (lap[`${coordinatePrefix}GpsLat`]) {
        lon = lap[`${coordinatePrefix}GpsLon`];
        lat = lap[`${coordinatePrefix}GpsLat`];
      } else {
        // 和APP 端保持一致，圈标记点无位置数据时取最近的有效点位置进行标记
        const nearestPoint = this.findNearestPoint(allActivties, lap[`${coordinatePrefix}Timestamp`]);
        lon = nearestPoint.gpsLon;
        lat = nearestPoint.gpsLat;
      }

      lap._markerGps = normalizeGps(lon, lat);
      return lap;
    };
    const originalLaps = data.find((item) => item.type === lapType2mark)?.lapItemList || [];
    this.laps = originalLaps.map(processLapData);

    // 钓鱼运动尚未支持，支持后需要保留最后一圈
    if (isSkiing) {
      return this;
    }

    this.laps.pop();

    return this;
  }

  setMap4markers(markers, map) {
    markers.forEach((marker) => {
      marker.setMap(map);
    });
  }

  clearLapMarkers() {
    this.allMarkers.forEach((marker) => {
      marker.setMap(null);
      if (this.isGoogleMap) {
        marker.setVisible(false);
        marker.setPosition(null);
      }
      marker = null;
    });

    this.allMarkers = [];
    return this;
  }

  /**
   * 渲染圈标记，节流规避切换运动清除圈标记问题
   */
  renderMarker4laps = (function () {
    let timeoutId;

    // 这里必须使用 timeout ，否则清除不了 marker
    return function () {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      const renderFn = () => {
        this.clearLapMarkers()
          .getRenderableMarkers()
          .then((markers) => {
            this.setMap4markers(markers, this.map);
            timeoutId = null;
          });
      };

      timeoutId = setTimeout(renderFn, 50);
    };
  })();
}
